import React, { useState, useEffect, useCallback } from 'react';
import { getCancellationPolicyConfig, updateCancellationPolicyConfig } from '../services/adminService';
import { pageContainer, cardStyle, primaryBtn, secondaryBtn, inputStyle } from '../styles/theme';
import { useToast } from '../utils/useAdminPage';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';

const DEFAULT_CONFIG: Record<string, string> = {
  user_cancel_tier1_hours: "24",
  user_cancel_tier2_hours: "2",
  user_cancel_tier2_fee_pct: "10",
  user_reschedule_cutoff_hours: "12",
  user_razorpay_fee_pct: "3",
  scholar_cancel_tier1_hours: "24",
  scholar_cancel_tier2_hours: "2",
  scholar_cancel_tier2_fee_pct: "15",
  scholar_cancel_tier2_flat_fee: "150",
  scholar_cancel_tier3_fee_pct: "30",
  scholar_cancel_tier3_flat_fee: "300",
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#334155',
  marginBottom: 6,
};

const hintStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  marginTop: 4,
};

const sectionHeader: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: '#0f172a',
  marginBottom: 16,
  borderBottom: '1px solid #e2e8f0',
  paddingBottom: 10,
};

const gridRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 20,
  marginBottom: 24,
};

const CancellationPolicyManager = () => {
  const [config, setConfig] = useState<Record<string, string>>({ ...DEFAULT_CONFIG });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast, showToast } = useToast();

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCancellationPolicyConfig();
      if (data) {
        setConfig({ ...DEFAULT_CONFIG, ...data });
      }
    } catch {
      showToast('Failed to load cancellation policy settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = (key: string, value: string) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    if (window.confirm('Reset all cancellation & refund policy settings to production defaults?')) {
      setConfig({ ...DEFAULT_CONFIG });
      showToast('Reverted parameters to production defaults');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCancellationPolicyConfig(config);
      showToast('Cancellation & Refund policy configuration saved successfully!');
      await fetchConfig();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save configuration';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />

      <PageHeader
        breadcrumb="Operations / Cancellation Policy"
        title="Cancellation & Refunds Policy Manager"
        subtitle="Manage dynamic cancellation cutoffs, refund percentages, scholar penalty tiers, and payment gateway disclaimers live across all mobile apps."
      />

      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 60, color: '#64748b' }}>
          Loading cancellation policy configurations…
        </div>
      ) : (
        <>
          {/* User Policy Card */}
          <div style={{ ...cardStyle, marginBottom: 24 }}>
            <div style={sectionHeader}>
              👤 Customer Cancellation & Refund Rules
            </div>

            <div style={gridRow}>
              <div>
                <label style={labelStyle}>Tier 1 Full Refund Cutoff (Hours)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.user_cancel_tier1_hours}
                  onChange={(e) => handleChange('user_cancel_tier1_hours', e.target.value)}
                />
                <div style={hintStyle}>Cancellations before this window receive 100% eligible refund (Default: 24h)</div>
              </div>

              <div>
                <label style={labelStyle}>Tier 2 Partial Refund Cutoff (Hours)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.user_cancel_tier2_hours}
                  onChange={(e) => handleChange('user_cancel_tier2_hours', e.target.value)}
                />
                <div style={hintStyle}>Cancellations below this cutoff are non-refundable (Default: 2h)</div>
              </div>

              <div>
                <label style={labelStyle}>Tier 2 Platform Cancellation Fee (%)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.user_cancel_tier2_fee_pct}
                  onChange={(e) => handleChange('user_cancel_tier2_fee_pct', e.target.value)}
                />
                <div style={hintStyle}>Percentage retained by platform for cancellations between 2h – 24h (Default: 10%)</div>
              </div>

              <div>
                <label style={labelStyle}>Rescheduling Minimum Cutoff (Hours)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.user_reschedule_cutoff_hours}
                  onChange={(e) => handleChange('user_reschedule_cutoff_hours', e.target.value)}
                />
                <div style={hintStyle}>Rescheduling disabled within this window (Default: 12h)</div>
              </div>

              <div>
                <label style={labelStyle}>Razorpay Payment Gateway Fee (%)</label>
                <input
                  type="number"
                  step="0.1"
                  style={inputStyle}
                  value={config.user_razorpay_fee_pct}
                  onChange={(e) => handleChange('user_razorpay_fee_pct', e.target.value)}
                />
                <div style={hintStyle}>Non-refundable transaction processing charge percentage (Default: 3%)</div>
              </div>
            </div>
          </div>

          {/* Scholar Policy Card */}
          <div style={{ ...cardStyle, marginBottom: 24 }}>
            <div style={sectionHeader}>
              👳 Scholar Cancellation & Penalty Ledger Rules
            </div>

            <div style={gridRow}>
              <div>
                <label style={labelStyle}>Free Cancellation Window (Hours)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.scholar_cancel_tier1_hours}
                  onChange={(e) => handleChange('scholar_cancel_tier1_hours', e.target.value)}
                />
                <div style={hintStyle}>Scholar cancellations &gt; this window incur ₹0 penalty (Default: 24h)</div>
              </div>

              <div>
                <label style={labelStyle}>Tier 2 Penalty Cutoff (Hours)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.scholar_cancel_tier2_hours}
                  onChange={(e) => handleChange('scholar_cancel_tier2_hours', e.target.value)}
                />
                <div style={hintStyle}>Cancellations between Tier 2 cutoff & Tier 1 incur Tier 2 penalty (Default: 2h)</div>
              </div>

              <div>
                <label style={labelStyle}>Tier 2 Scholar Penalty Rate (%)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.scholar_cancel_tier2_fee_pct}
                  onChange={(e) => handleChange('scholar_cancel_tier2_fee_pct', e.target.value)}
                />
                <div style={hintStyle}>Percentage penalty for Tier 2 scholar cancellations (Default: 15%)</div>
              </div>

              <div>
                <label style={labelStyle}>Tier 2 Minimum Flat Fee (₹)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.scholar_cancel_tier2_flat_fee}
                  onChange={(e) => handleChange('scholar_cancel_tier2_flat_fee', e.target.value)}
                />
                <div style={hintStyle}>Minimum flat penalty fee levied for Tier 2 cancellations (Default: ₹150)</div>
              </div>

              <div>
                <label style={labelStyle}>Tier 3 Last-Minute Penalty Rate (%)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.scholar_cancel_tier3_fee_pct}
                  onChange={(e) => handleChange('scholar_cancel_tier3_fee_pct', e.target.value)}
                />
                <div style={hintStyle}>Percentage penalty for cancellations &lt; 2h or No-Shows (Default: 30%)</div>
              </div>

              <div>
                <label style={labelStyle}>Tier 3 Minimum Flat Fee (₹)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={config.scholar_cancel_tier3_flat_fee}
                  onChange={(e) => handleChange('scholar_cancel_tier3_flat_fee', e.target.value)}
                />
                <div style={hintStyle}>Minimum flat penalty fee for last-minute cancellations / No-shows (Default: ₹300)</div>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button style={secondaryBtn} onClick={handleReset} disabled={saving}>
              Reset to Defaults
            </button>
            <button style={primaryBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving Changes…' : 'Save Policy Changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CancellationPolicyManager;
